import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Button } from 'components/ui/Button/Button'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { getLiveTasks } from 'features/operations/opsLiveTasksSlice'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Flex, H1WithBack, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText } from 'styling/Typography'
import { IOpsLiveTask } from 'types/operationsControl.interface'
import { SOURCE_META, taskCoreState } from '../../Shared/opsControl'
import { CoreStatePill } from '../../Shared/OpsSharedComponents'
import { EvenCell, EvenTableWrapper } from './OperationsTasks.styled'
import { AddTaskModal } from './AddTaskModal'

const columns: IHonestTableColumn<IOpsLiveTask>[] = [
  {
    id: 'name',
    header: 'Task',
    sortable: true,
    sticky: true,
    cell: (item) => (
      <EvenCell>
        <BodyText weight='600'>{item.name}</BodyText>
      </EvenCell>
    ),
  },
  {
    id: 'department',
    header: 'Department',
    sortable: true,
    cell: (item) => (
      <EvenCell>
        <BodyText>{item.department || '—'}</BodyText>
      </EvenCell>
    ),
  },
  {
    id: 'location',
    header: 'Location',
    sortable: true,
    cell: (item) => (
      <EvenCell>
        <BodyText>{item.location ?? '—'}</BodyText>
      </EvenCell>
    ),
  },
  {
    id: 'source',
    header: 'Source',
    sortable: true,
    cell: (item) => (
      <EvenCell>
        <StatusPill status={SOURCE_META[item.source].tone}>{SOURCE_META[item.source].label}</StatusPill>
      </EvenCell>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    cell: (item) => (
      <EvenCell>
        <CoreStatePill state={taskCoreState(item.taskStatus)} context='task' />
      </EvenCell>
    ),
  },
  {
    id: 'operator',
    header: 'Operator',
    cell: (item) => (
      <EvenCell>
        <BodyText>{item.operator ?? '—'}</BodyText>
        {item.machine && <OtherText grey>{item.machine}</OtherText>}
      </EvenCell>
    ),
  },
  {
    id: 'generatedAt',
    header: 'Generated',
    sortable: true,
    cell: (item) => (
      <EvenCell>
        <BodyText>{item.generatedAt || '—'}</BodyText>
      </EvenCell>
    ),
  },
]

export const OperationsTasks = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { tasks, isLoading } = useSelector((state: RootState) => state.opsLiveTasks)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    dispatch(getLiveTasks({}))
  }, [dispatch])

  const rows = useMemo(() => tasks, [tasks])

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex between width='100%' mb='16'>
          <H1WithBack arrow title='Tasks' />
          <Button variant='primary' onClick={() => setModalOpen(true)}>
            Add
          </Button>
        </Flex>
        <EvenTableWrapper>
          {!isLoading && rows.length === 0 ? (
            <NoData text='No live tasks yet. Create a manual task or activate a plan to generate work.' />
          ) : (
            <HonestTable columns={columns} data={rows} keyField='id' emptyMessage='No tasks found.' />
          )}
        </EvenTableWrapper>
      </PageWrapper>
      {modalOpen && (
        <AddTaskModal
          closeModal={() => setModalOpen(false)}
          onCreated={() => dispatch(getLiveTasks({}))}
        />
      )}
    </>
  )
}
